import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import Loading from '../../components/common/Loading';
import MobileAppsNav from '../../components/common/MobileAppsNav';
import {
  FaBox,
  FaSignOutAlt,
  FaCogs,
  FaTruck
} from 'react-icons/fa';
import './InventoryDashboard.css';
import logo from '../../assets/img.jpg';

const InventoryDashboard = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const { data: dashboardData, isLoading, error } = useQuery(
    'inventoryDashboard',
    () => api.get('/api/inventory/dashboard'),
    {
      staleTime: 2 * 60 * 1000, // 2 minutes
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    }
  );

  const [modalType, setModalType] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState(null);
  const [manufacturingCategoryCount, setManufacturingCategoryCount] = useState(0);
  const [vendorCount, setVendorCount] = useState(0);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Load count of manufacturing categories and vendors for dashboard cards
  React.useEffect(() => {
    const loadCounts = async () => {
      try {
        const catRes = await api.get('/api/manufacturing-categories');
        const catList = catRes.data?.manufacturingCategories || [];
        setManufacturingCategoryCount(Array.isArray(catList) ? catList.length : 0);
      } catch (err) {
        setManufacturingCategoryCount(0);
      }
      try {
        const vendorRes = await api.get('/api/vendors');
        const vendorList = vendorRes.data?.vendors || [];
        setVendorCount(Array.isArray(vendorList) ? vendorList.length : 0);
      } catch (err) {
        setVendorCount(0);
      }
    };
    loadCounts();
  }, []);

  // Fetch data for modal when a card is clicked
  const handleViewClick = async (type) => {
    setModalType(type);
    setShowModal(true);
    setModalLoading(true);
    setModalError(null);
    let url = '';
    let key = '';

    if (type === 'inventory') {
      url = '/api/inventory';
      key = 'inventoryItems';
    } else if (type === 'companies') {
      url = '/api/companies';
      key = 'companies';
    } else if (type === 'billings') {
      url = '/api/billing';
      key = 'billings';
    } else if (type === 'quotations') {
      url = '/api/quotations';
      key = 'quotations';
    } else if (type === 'customers') {
      url = '/api/customers';
      key = 'customers';
    } else if (type === 'returns') {
      url = '/api/returns';
      key = 'returns';
    } else if (type === 'manufacturing-categories') {
      url = '/api/manufacturing-categories';
      key = 'manufacturingCategories';
    } else if (type === 'vendors') {
      url = '/api/vendors';
      key = 'vendors';
    } else {
      return;
    }

    try {
      const res = await api.get(url);
      setModalData(res.data[key] || res.data);
    } catch (err) {
      setModalError('Failed to fetch data.');
      setModalData([]);
    } finally {
      setModalLoading(false);
    }
  };

  // Axios wraps server response in .data; server payload uses { data: { ... } }
  const payload = dashboardData?.data?.data;
  const stats = payload?.stats || {};
  const recentInventory = payload?.recentInventory || [];
  // const recentBillings = payload?.recentBillings || [];

  // Modal content for each card
  const renderModalContent = () => {
    if (modalLoading) return <Loading />;
    if (modalError) return <div style={{ color: 'red' }}>{modalError}</div>;

    if (modalType === 'inventory') {
      return (
        <div>
          <h2>All Inventory Items</h2>
          {modalData.length === 0 ? <p>No inventory items found.</p> : (
            <table className="modal-table">
              <thead>
                <tr>
                  <th>Item Code</th>
                  <th>Item Name</th>
                  <th>Company</th>
                  <th>Category</th>
                  <th>Quantity</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {modalData.map(item => (
                  <tr key={item._id}>
                    <td>{item.itemCode}</td>
                    <td>{item.itemName}</td>
                    <td>{item.company?.name || 'N/A'}</td>
                    <td>{item.category}</td>
                    <td>{item.itemQuantity}</td>
                    <td>{item.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      );
    }

    if (modalType === 'companies') {
      return (
        <div>
          <h2>All Companies</h2>
          {modalData.length === 0 ? <p>No companies found.</p> : (
            <table className="modal-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Contact Person</th>
                  <th>Email</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {modalData.map(company => (
                  <tr key={company._id}>
                    <td>{company.name}</td>
                    <td>{company.assignedTo?.name || company.contactPerson || 'N/A'}</td>
                    <td>{company.contact?.email || company.contactEmail || 'N/A'}</td>
                    <td>{company.status || 'active'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      );
    }

    if (modalType === 'billings') {
      return (
        <div>
          <h2>All Billings</h2>
          {modalData.length === 0 ? <p>No billings found.</p> : (
            <table className="modal-table">
              <thead>
                <tr>
                  <th>Billing No</th>
                  <th>Company</th>
                  <th>Total Amount</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {modalData.map(billing => (
                  <tr key={billing._id}>
                    <td>{billing.billingNo}</td>
                    <td>{billing.company?.name || 'N/A'}</td>
                    <td>₹{billing.totalAmount?.toFixed(2)}</td>
                    <td>{billing.status}</td>
                    <td>{new Date(billing.createdAt).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      );
    }

    if (modalType === 'quotations') {
      return (
        <div>
          <h2>All Quotations</h2>
          {modalData.length === 0 ? <p>No quotations found.</p> : (
            <table className="modal-table">
              <thead>
                <tr>
                  <th>Quotation No</th>
                  <th>Company</th>
                  <th>Total Amount</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {modalData.map(quotation => (
                  <tr key={quotation._id}>
                    <td>{quotation.quotationNo}</td>
                    <td>{quotation.company?.name || 'N/A'}</td>
                    <td>₹{quotation.totalAmount?.toFixed(2)}</td>
                    <td>{quotation.status}</td>
                    <td>{new Date(quotation.createdAt).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      );
    }

    if (modalType === 'customers') {
      return (
        <div>
          <h2>All Customers</h2>
          {modalData.length === 0 ? <p>No customers found.</p> : (
            <table className="modal-table">
              <thead>
                <tr>
                  <th>Customer Code</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Type</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {modalData.map(customer => (
                  <tr key={customer._id}>
                    <td>{customer.customerCode}</td>
                    <td>{customer.firstName} {customer.lastName}</td>
                    <td>{customer.email}</td>
                    <td>{customer.customerType}</td>
                    <td>{customer.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      );
    }

    if (modalType === 'returns') {
      return (
        <div>
          <h2>Returned Products</h2>
          {modalData.length === 0 ? <p>No returns found.</p> : (
            <table className="modal-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Item</th>
                  <th>Code</th>
                  <th>Qty</th>
                  <th>Condition</th>
                  <th>Reason</th>
                  <th>Received By</th>
                </tr>
              </thead>
              <tbody>
                {modalData.map(r => (
                  <tr key={r._id}>
                    <td>{new Date(r.createdAt).toLocaleString()}</td>
                    <td>{r.itemName}</td>
                    <td>{r.itemCode}</td>
                    <td>{r.quantity}</td>
                    <td>{r.condition}</td>
                    <td>{r.reason || '-'}</td>
                    <td>{r.receivedBy?.name || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      );
    }
    if (modalType === 'manufacturing-categories') {
      return (
        <div>
          <h2>Manufacturing Categories</h2>
          {modalData.length === 0 ? <p>No manufacturing categories found.</p> : (
            <table className="modal-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Code</th>
                  <th>Description</th>
                  <th>Status</th>
                  <th>Created By</th>
                </tr>
              </thead>
              <tbody>
                {modalData.map(category => (
                  <tr key={category._id}>
                    <td>{category.name}</td>
                    <td>{category.code}</td>
                    <td>{category.description || '-'}</td>
                    <td>{category.status}</td>
                    <td>{category.createdBy?.name || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      );
    }

    if (modalType === 'vendors') {
      return (
        <div>
          <h2>All Vendors</h2>
          {modalData.length === 0 ? <p>No vendors found.</p> : (
            <table className="modal-table">
              <thead>
                <tr>
                  <th>Vendor Name</th>
                  <th>Contact Person</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Assigned To</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {modalData.map(vendor => (
                  <tr key={vendor._id}>
                    <td>{vendor.name}</td>
                    <td>{vendor.contactPerson || vendor.assignedTo?.name || 'N/A'}</td>
                    <td>{vendor.contact?.email || vendor.contactEmail || 'N/A'}</td>
                    <td>{vendor.contact?.phone || 'N/A'}</td>
                    <td>{vendor.assignedTo?.name || 'N/A'}</td>
                    <td>{vendor.status || 'active'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      );
    }

    return null;
  };

  return (
    <div className="dashboard-container">
      {/* Mobile Navigation (Inventory Portal) */}
      <MobileAppsNav portal="inventory" />

      {/* Desktop Sidebar */}
      <aside className="dashboard-sidebar">
        <img src={logo} alt="AVS Business Logo" className="sidebar-logo" />
        <h2>Inventory Menu</h2>
        <nav>
          <ul>
            <li>
              <Link to="/apps.avsbusiness.com/inventory"><FaBox style={{ marginRight: '10px', verticalAlign: 'middle' }} />Inventory</Link>
            </li>
            <li>
              <Link to="/apps.avsbusiness.com/inventory/vendors"><FaTruck style={{ marginRight: '10px', verticalAlign: 'middle' }} />Vendors</Link>
            </li>
            <li>
              <Link to="/apps.avsbusiness.com/inventory/manufacturing-categories"><FaCogs style={{ marginRight: '10px', verticalAlign: 'middle' }} />Manufacturing Categories</Link>
            </li>
          </ul>
        </nav>
        <div className="sidebar-logout">
          <button
            onClick={handleLogout}
            className="logout-button"
            style={{
              background: 'linear-gradient(135deg,#ef4444,#f97316)',
              color: '#ffffff',
              border: 'none',
              borderRadius: '8px',
              padding: '12px 20px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              width: '100%',
              justifyContent: 'center',
              transition: 'transform 0.15s ease, box-shadow 0.15s ease',
              boxShadow: '0 4px 12px rgba(239,68,68,0.3)'
            }}
            onMouseEnter={e => {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 6px 16px rgba(239,68,68,0.4)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(239,68,68,0.3)';
            }}
          >
            <FaSignOutAlt />
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="dashboard-main">
        <h1>AVS BUSINESS Inventory Dashboard</h1>
        <p>Manage your inventory operations and stock levels</p>

        {isLoading && <Loading />}
        {error && (
          <div className="error-message" style={{
            padding: '20px',
            backgroundColor: '#fee',
            color: '#c33',
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            Error loading dashboard data: {error.response?.data?.message || error.message || 'Unknown error'}
          </div>
        )}

        {/* Stats Cards */}
        <div className="dashboard-cards">
          <div className="dashboard-card" role="button" tabIndex={0} onClick={() => handleViewClick('inventory')} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleViewClick('inventory'); } }}>
            <div className="card-title">Inventory Items</div>
            <div className="card-value">{stats.totalInventoryItems || 0}</div>
            <button className="card-link" type="button">View</button>
          </div>
          <div className="dashboard-card" role="button" tabIndex={0} onClick={() => handleViewClick('vendors')} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleViewClick('vendors'); } }}>
            <div className="card-title">Vendors</div>
            <div className="card-value">{vendorCount}</div>
            <button className="card-link" type="button">View</button>
          </div>
          <div className="dashboard-card" role="button" tabIndex={0} onClick={() => handleViewClick('manufacturing-categories')} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleViewClick('manufacturing-categories'); } }}>
            <div className="card-title">Manufacturing Categories</div>
            <div className="card-value">{manufacturingCategoryCount}</div>
            <button className="card-link" type="button">View</button>
          </div>
        </div>

        <div className="dashboard-section">
          <h2>Recent Inventory Items</h2>
          <div className="section-content">
            {(recentInventory.length === 0) ? (
              <p>No recent inventory items</p>
            ) : (
              <div>
                {recentInventory.slice(0, 5).map((item) => (
                  <div key={item._id}>
                    {item.itemCode} - {item.itemName} - Qty: {item.itemQuantity} - {item.company?.name || 'Unknown Company'}
                  </div>
                ))}
              </div>
            )}
            <Link to="/apps.avsbusiness.com/inventory" className="section-link">
              View All Inventory Items
            </Link>
          </div>
        </div>


        {/* Modal for details */}
        {showModal && (
          <div className="dashboard-modal-overlay" onClick={() => setShowModal(false)}>
            <div className="dashboard-modal" onClick={e => e.stopPropagation()}>
              <button className="dashboard-modal-close" onClick={() => setShowModal(false)}>&times;</button>
              {renderModalContent()}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default InventoryDashboard;